/**
 * CORE VARIABLE BLOCKS — definitions + JS generators, extracted verbatim from
 * blockly-interface.component.ts so they can be registered WITHOUT an Angular component.
 *
 * WHY THIS FILE EXISTS
 * The background generation pipeline (docs/plans/background-generation-pipeline.md) runs the real
 * steps -> blocks -> {code, xml} chain in node. Every other block family already lives in a helper
 * (query-helper, entity-variable-healper, special-functions-helper, condition-blocks, ...) and
 * therefore already bundles for node. These thirteen were the only ones still inside the component,
 * which is the entire reason the batch generator had to live there too:
 *
 *   "It lives HERE, in the function editor, because the steps->{code,xml} conversion is not headless."
 *   -- blockly-interface.component.ts, forgeGenerateEmptyBodies
 *
 * NOTHING IS REMOVED. The component keeps all thirteen methods; each now DELEGATES here, the same
 * pattern isSystemVariableKey uses to delegate to atlas.utils. One definition, two callers:
 * the editor component and the headless runner. They cannot drift, because they are the same code.
 *
 * THE HOST SEAM
 * Bodies referenced the component ONLY through `const componentRef: any = this;` -- every other
 * `this.` in them is Blockly's own (a Block inside init(), a Field, or the RUNTIME `this` inside an
 * emitted code template). So the extraction is exactly one substitution: `this` -> `host`.
 *
 * `host` is the editor component in the browser. Headless, it is a stub: the only members reached are
 * the Atlas-picker path (selectedBlock, selectedBlockFlag, variableToBeAdded, presentBlockVariable,
 * blockAtlasField, openAtlasForBlock, openPushFieldPicker, deletePushRow, resolvePushTargetEntityId),
 * none of which fire during headless compilation -- they are click handlers.
 */
import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';

/**
 * What the extracted blocks reach for on their host. Deliberately all-optional: a headless runner
 * supplies `{}` and the picker paths simply never fire (they are showEditor_/click handlers).
 */
export interface CoreVariableBlocksHost {
  selectedBlock?: any;
  selectedBlockFlag?: boolean;
  variableToBeAdded?: any;
  presentBlockVariable?: any;
  // `blockAtlasField` is reached too, but is NOT declared: it is `private` on the component, and a
  // public optional here would make `this` structurally incompatible. The index signature covers it.
  openAtlasForBlock?: () => void;
  openPushFieldPicker?: (block: any) => void;
  deletePushRow?: (block: any, key: string) => void;
  resolvePushTargetEntityId?: (v: string) => string;
  [k: string]: any;
}

/**
 * When a variable block (get/set, plain or AOB reference) re-applies its variable's type and colour
 * (zavo_bug_oct_12): on FINISHED_LOADING (opening a function), and on its OWN creation, a BLOCK_CREATE that names
 * it. That BLOCK_CREATE is the only block event a paste (Ctrl+V / Duplicate) fires. The VAR validator cannot do it: a
 * FieldVariable skips its validator on its first setValue, which is the restore. Both events are delivered after
 * the blocks are fully built, never during deserialization.
 */
function reappliesType(e: any, block: any): boolean {
  const E: any = (Blockly as any).Events;
  return e.type === E.FINISHED_LOADING ||
    (e.type === E.BLOCK_CREATE && Array.isArray(e.ids) && e.ids.includes(block.id));
}


export function customBlockForSetVariable(host: CoreVariableBlocksHost) {
    const SCALAR_VAR_TYPES = ['String', 'Number', 'Boolean', 'TimeStamp', 'GEOMETRY'];
    // type -> VALUE-slot check (mirrors the old per-type setChecks exactly)
    const VAR_VALUE_CHECK: { [k: string]: string[] } = {
      String: ['String', 'Number'],
      Number: ['Number'],
      Boolean: ['Boolean'],
      TimeStamp: ['TimeStamp'],
      // A GEOMETRY slot takes geometry, or a String holding GeoJSON — a builder may keep it in a plain
      // STRING column (contract §5 mandates no particular type). Same reasoning as polygon_area's socket.
      GEOMETRY: ['GEOMETRY', 'String'],
    };
    // type -> RHS template (byte-exact parity with the old set generators)
    const VAR_SET_RHS: { [k: string]: (v: string) => string } = {
      Number: (v) => ' = ' + v,
      Boolean: (v) => ' =' + v,
      String: (v) => " =  '' + " + v,
      TimeStamp: (v) => " =  '' + " + v,
      // Pass through unchanged. NOT the "'' + v" of String/TimeStamp: geometry is an object, so
      // stringifying yields "[object Object]", and JSON text into a jsonb column double-encodes.
      GEOMETRY: (v) => ' = ' + v,
    };
    // REQUIRED: recolour to the type's colour on every change (= old per-type colours)
    const VAR_COLOUR: { [k: string]: any } = {
      Number: 230, String: '#5ba58c', Boolean: 0, TimeStamp: '#F5A623', GEOMETRY: '#2e7d32',
    };

    const componentRef: any = host;
    class NonEditableFieldVariable extends Blockly.FieldVariable {
      // Clicking the variable opens Atlas (the real picker) instead of the inert
      // native dropdown — so the caret is a working affordance, not a dead control.
      override showEditor_() {
        const blk: any = this.getSourceBlock();
        if (!blk || !componentRef) return;
        componentRef.selectedBlock = blk;
        componentRef.selectedBlockFlag = true;
        componentRef.variableToBeAdded = componentRef.variableToBeAdded || {};
        componentRef.variableToBeAdded.allowedType = 'SCALAR';
        componentRef.presentBlockVariable = blk.getFieldValue('VAR');
        componentRef.openAtlasForBlock();
      }
    }

    Blockly.Blocks['set_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .appendField('set')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$default',
              undefined,
              SCALAR_VAR_TYPES,
              'String'
            ),
            'VAR'
          )
          .appendField('to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setTooltip('Set a value to a variable (type follows the variable)');
        this.setHelpUrl('');

        const block: any = this;
        const applyType = function (id: any) {
          const v = block.workspace && block.workspace.getVariableById(id);
          const t = v && v.type;
          // neutral grey while unconfigured (default placeholder / unknown type) so a
          // fresh block does NOT misleadingly look like a String (green).
          const configured = !!(v && v.name !== 'localVariable$default' && VAR_COLOUR[t] != null);
          // NULL check when unconfigured/loading so saved value blocks (incl. TimeStamp/Boolean outputs
          // like current_date_iso / date_add_days) RECONNECT to ANY socket during deserialization —
          // mirrors get_variable's null-output trick. init runs applyType before the VAR field is
          // restored, so the old ['String','Number'] fallback rejected TimeStamp/Boolean children and
          // dropped them on reload. The real check is re-applied on pick (validator), on FINISHED_LOADING and on
          // this block's own BLOCK_CREATE (a paste), see reappliesType.
          block.getInput('VALUE').setCheck(configured ? VAR_VALUE_CHECK[t] : null);
          block.setColour(configured ? VAR_COLOUR[t] : '#9aa0a6');
        };
        this.getField('VAR').setValidator(function (newId: any) {
          applyType(newId);
          return newId;
        });
        applyType(this.getFieldValue('VAR'));
        // Re-apply the type after load or paste (the validator doesn't fire on field-restore) on FINISHED_LOADING or
        // this block's own BLOCK_CREATE (reappliesType) — NOT during deserialization. Refreshes colour + the VALUE
        // socket's check for the saved value.
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          if (reappliesType(e, block)) applyType(block.getFieldValue('VAR'));
          // Was (until 2026-10-09): if (e.type === (Blockly as any).Events.FINISHED_LOADING) applyType(block.getFieldValue('VAR'));
          // — a paste fires no FINISHED_LOADING, so a pasted block stayed grey and untyped until the function was reopened.
        });
      },
    };

    (javascriptGenerator as any).forBlock['set_variable'] = function (block: any) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      var value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '0';
      var v =
        block.workspace && block.workspace.getVariableById(block.getFieldValue('VAR'));
      var t = (v && v.type) || 'Number';
      // A null value must land as a TRUE null, not be coerced: the String/TimeStamp RHS does `'' + value`,
      // which turns null into the string "null". Detect the logic_null child and assign raw null.
      var valueBlock = block.getInputTargetBlock('VALUE');
      var rhs = (valueBlock && valueBlock.type === 'logic_null')
        ? ' = null'
        : (VAR_SET_RHS[t] || VAR_SET_RHS['Number'])(value);
      return variableName + rhs + ';\n';
    };
}

export function customBlockForPushNewElementToArrayV2(host: CoreVariableBlocksHost) {
    const componentRef: any = host;
    const TYPE_CHECK: any = { String: ['String'], Number: ['Number'], Boolean: ['Boolean'], TimeStamp: ['TimeStamp'] };
    const slotName = (key: string) => 'FIELD_' + String(key).replace(/[^a-zA-Z0-9_]/g, '_');

    class ClickField extends Blockly.FieldTextInput {
      clickFn_: any;
      constructor(text: string, onClick: any) { super(text); this.clickFn_ = onClick; }
      // clicking opens the picker / deletes the row instead of editing the text
      override showEditor_() { if (this.clickFn_) this.clickFn_(); }
    }

    (Blockly as any).Blocks['push_new_element_to_array'] = {
      init: function (this: any) {
        const block: any = this;
        this.appendValueInput('ARRAY_INPUT')
          .setCheck(['Array', 'REFERENCE_ENTITY_VARIABLE_AOB'])
          .appendField('push to array');
        // optional insert position — empty/weird/out-of-range falls back to appending at the end
        this.appendValueInput('INDEX_INPUT')
          .setCheck(['Number'])
          .appendField('at index (optional)')
          .appendField(new ClickField('ⓘ', () => (window as any).alert(
            'Insert position:\n• in-range index → inserts at that position\n• empty or out-of-range → appends at the end'
          )), 'IDX_INFO');
        this.appendDummyInput('ADDFIELD')
          .appendField(new ClickField('＋ set field', () => componentRef.openPushFieldPicker(block)), 'ADD');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4B0082');
        this.setTooltip('Push a new row to the list/AOB; optionally set its fields.');
        this.setInputsInline(false); // each field row on its own line (not side-by-side)
        this.fieldRows_ = [];
        // re-apply the real socket checks AFTER load (never during deserialization).
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          if (e.type === (Blockly as any).Events.FINISHED_LOADING) {
            for (const r of (block.fieldRows_ || [])) {
              const inp = block.getInput(r.inputName);
              if (inp) inp.setCheck(TYPE_CHECK[r.type] || null);
            }
          }
        });
      },
      mutationToDom: function (this: any) {
        const m = (Blockly as any).utils.xml.createElement('mutation');
        for (const r of (this.fieldRows_ || [])) {
          const f = (Blockly as any).utils.xml.createElement('f');
          f.setAttribute('key', r.key);
          f.setAttribute('vtype', r.type);
          f.setAttribute('lbl', r.label || r.key);
          m.appendChild(f);
        }
        return m;
      },
      domToMutation: function (this: any, xml: any) {
        for (const inp of this.inputList.slice()) {
          if (inp.name && inp.name.indexOf('FIELD_') === 0) this.removeInput(inp.name);
        }
        this.fieldRows_ = [];
        const fs = xml && xml.getElementsByTagName ? xml.getElementsByTagName('f') : [];
        for (let i = 0; i < fs.length; i++) {
          // permissive (setCheck null) so saved value blocks reconnect; real check on FINISHED_LOADING
          this.addFieldRow_(fs[i].getAttribute('key'), fs[i].getAttribute('vtype'), fs[i].getAttribute('lbl') || fs[i].getAttribute('key'), true);
        }
      },
      addFieldRow_: function (this: any, key: string, type: string, label: string, permissive: boolean) {
        const block: any = this;
        const name = slotName(key);
        if (this.getInput(name)) return;
        const inp = this.appendValueInput(name)
          .appendField(new ClickField('✕', () => componentRef.deletePushRow(block, key)), 'DEL_' + name)
          .appendField(label || key);
        inp.setCheck(permissive ? null : (TYPE_CHECK[type] || null));
        if (this.getInput('ADDFIELD') && this.moveInputBefore) this.moveInputBefore(name, 'ADDFIELD');
        this.fieldRows_.push({ key, type, label: label || key, inputName: name });
      },
      removeFieldRow_: function (this: any, key: string) {
        const name = slotName(key);
        const inp = this.getInput(name);
        if (inp) {
          const tb = inp.connection && inp.connection.targetBlock && inp.connection.targetBlock();
          if (tb) tb.unplug(true);
          this.removeInput(name);
        }
        this.fieldRows_ = (this.fieldRows_ || []).filter((r: any) => r.key !== key);
      },
    };

    (javascriptGenerator as any).forBlock['push_new_element_to_array'] = function (block: any) {
      let arrayInput = (javascriptGenerator as any).valueToCode(block, 'ARRAY_INPUT', (javascriptGenerator as any).ORDER_MEMBER) || '[]';
      if (arrayInput.includes('entity_reference_for_AOB')) {
        arrayInput = arrayInput.replace(/\[.*?\]/g, '').replace(/\$$/, '');
        const dollarInside = (arrayInput.replace('entity_reference_for_AOB$', '')).includes('$');
        if (!dollarInside) arrayInput = arrayInput + '$';
      }
      const rows = (block.fieldRows_ || []);
      const tmp = '__pushRow_' + String(block.id).replace(/[^a-zA-Z0-9_]/g, '');
      // optional insert index → passed to getArrayElement (which splices/clamps); empty ⇒ append.
      const idxCode = (javascriptGenerator as any).valueToCode(block, 'INDEX_INPUT', (javascriptGenerator as any).ORDER_NONE);
      const idxArg = idxCode ? `, ${idxCode}` : '';
      // push (getArrayElement) → fill the returned row → THEN flip the re-render flag, so the
      // UI only ever paints the completed row, never the blank default one.
      let code = `const ${tmp} = await this.getArrayElement('${arrayInput}'${idxArg});`;
      for (const r of rows) {
        const v = (javascriptGenerator as any).valueToCode(block, r.inputName, (javascriptGenerator as any).ORDER_ASSIGNMENT);
        if (!v) continue; // no value plugged in → schema default prevails
        if (String(r.key).includes('.')) {
          const a = r.key.split('.')[0];
          const b = r.key.split('.')[1];
          code += ` if(${tmp} && ${tmp}['${a}']) ${tmp}['${a}']['${b}'] = ${v};`;
        } else {
          code += ` if(${tmp}) ${tmp}['${r.key}'] = ${v};`;
        }
      }
      code += ` this.reinitializeComponantsFlag = true;`;
      return code + '\n';
    };
}

export function customBlockForGetVariable(host: CoreVariableBlocksHost) {
    const SCALAR_VAR_TYPES = ['String', 'Number', 'Boolean', 'TimeStamp', 'GEOMETRY'];
    const VAR_COLOUR: { [k: string]: any } = {
      Number: 230, String: '#5ba58c', Boolean: 0, TimeStamp: '#F5A623', GEOMETRY: '#2e7d32',
    };

    const componentRef: any = host;
    class NonEditableFieldVariable extends Blockly.FieldVariable {
      // Clicking the variable opens Atlas (the real picker) instead of the inert
      // native dropdown — so the caret is a working affordance, not a dead control.
      override showEditor_() {
        const blk: any = this.getSourceBlock();
        if (!blk || !componentRef) return;
        componentRef.selectedBlock = blk;
        componentRef.selectedBlockFlag = true;
        componentRef.variableToBeAdded = componentRef.variableToBeAdded || {};
        componentRef.variableToBeAdded.allowedType = 'SCALAR';
        componentRef.presentBlockVariable = blk.getFieldValue('VAR');
        componentRef.openAtlasForBlock();
      }
    }

    Blockly.Blocks['get_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$default',
              undefined,
              SCALAR_VAR_TYPES,
              'String'
            ),
            'VAR'
          );
        this.setOutput(true, 'String');
        this.setTooltip('Get the value of a variable (type follows the variable)');
        this.setHelpUrl('');

        const block: any = this;
        const applyType = function (id: any) {
          const v = block.workspace && block.workspace.getVariableById(id);
          const configured = !!(v && v.name !== 'localVariable$default' && VAR_COLOUR[v.type] != null);
          // NULL output when unconfigured/loading so a saved get RECONNECTS to ANY socket on load
          // (Blockly drops a value block whose output type mismatches its socket during deserialization).
          // The real type is applied on pick (validator), and on FINISHED_LOADING or this block's own BLOCK_CREATE below.
          block.setOutput(true, configured ? v.type : null);
          block.setColour(configured ? VAR_COLOUR[v.type] : '#9aa0a6');
        };
        this.getField('VAR').setValidator(function (newId: any) {
          applyType(newId);
          return newId;
        });
        applyType(this.getFieldValue('VAR'));
        // Re-apply the type AFTER load or paste — the VAR field is restored without firing the validator. Done on
        // FINISHED_LOADING or this block's own BLOCK_CREATE (reappliesType), NOT during fromXml/deserialization
        // (mutating the block then corrupts/deletes it).
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          if (reappliesType(e, block)) applyType(block.getFieldValue('VAR'));
          // Was (until 2026-10-09): if (e.type === (Blockly as any).Events.FINISHED_LOADING) applyType(block.getFieldValue('VAR'));
          // — a paste fires no FINISHED_LOADING, so a pasted block stayed grey and untyped until the function was reopened.
        });
      },
    };

    (javascriptGenerator as any).forBlock['get_variable'] = function (block: any) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };
}

export function customBlockForSetAobVariable(host: CoreVariableBlocksHost) {
    const VAR_VALUE_CHECK: { [k: string]: string[] } = {
      String: ['String', 'Number'], Number: ['Number'],
      Boolean: ['Boolean'], TimeStamp: ['TimeStamp'],
    };
    const VAR_SET_RHS: { [k: string]: (v: string) => string } = {
      Number: (v) => ' = ' + v, Boolean: (v) => ' =' + v,
      String: (v) => " =  '' + " + v, TimeStamp: (v) => " =  '' + " + v,
    };
    const VAR_COLOUR: { [k: string]: any } = {
      Number: 230, String: '#5ba58c', Boolean: 0, TimeStamp: '#F5A623', GEOMETRY: '#2e7d32',
    };
    const componentRef: any = host;
    class NonEditableFieldVariable extends Blockly.FieldVariable {
      override showEditor_() {
        const blk: any = this.getSourceBlock();
        if (!blk || !componentRef) return;
        componentRef.selectedBlock = blk;
        componentRef.selectedBlockFlag = true;
        componentRef.variableToBeAdded = componentRef.variableToBeAdded || {};
        componentRef.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
        componentRef.variableToBeAdded.entity = 'entity_reference_for_AOB';
        componentRef.AOBToBeAddedAllowedType = 'SCALAR';
        componentRef.AOBToBeAddedEntity = [];
        componentRef.blockAtlasField = 'VAR';
        componentRef.presentBlockVariable = blk.getFieldValue('VAR');
        componentRef.openAtlasForBlock();
      }
    }

    Blockly.Blocks['set_aob_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .appendField('set')
          .appendField(
            new NonEditableFieldVariable('NAN', undefined, ['String', 'Number', 'Boolean', 'TimeStamp'], 'String'),
            'VAR'
          )
          .appendField('FROM AOB reference to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setTooltip('Set a value to an AOB-reference field (type follows the field)');
        this.setHelpUrl('');

        const block: any = this;
        const applyType = function (id: any) {
          const v = block.workspace && block.workspace.getVariableById(id);
          const t = v && v.type;
          const configured = !!(v && v.name !== 'NAN' && VAR_COLOUR[t] != null);
          // NULL check when unconfigured/loading so saved value blocks (incl. TimeStamp/Boolean outputs
          // like current_date_iso / date_add_days) RECONNECT to ANY socket during deserialization —
          // mirrors get_variable's null-output trick. init runs applyType before the VAR field is
          // restored, so the old ['String','Number'] fallback rejected TimeStamp/Boolean children and
          // dropped them on reload. The real check is re-applied on pick (validator), on FINISHED_LOADING and on
          // this block's own BLOCK_CREATE (a paste), see reappliesType.
          block.getInput('VALUE').setCheck(configured ? VAR_VALUE_CHECK[t] : null);
          block.setColour(configured ? VAR_COLOUR[t] : '#9aa0a6');
        };
        this.getField('VAR').setValidator(function (newId: any) {
          applyType(newId);
          return newId;
        });
        applyType(this.getFieldValue('VAR'));
        // Re-apply the type after load or paste, on FINISHED_LOADING or this block's own BLOCK_CREATE (reappliesType),
        // NOT during deserialization.
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          if (reappliesType(e, block)) applyType(block.getFieldValue('VAR'));
          // Was (until 2026-10-09): if (e.type === (Blockly as any).Events.FINISHED_LOADING) applyType(block.getFieldValue('VAR'));
          // — a paste fires no FINISHED_LOADING, so a pasted block stayed grey and untyped until the function was reopened.
        });
      },
    };

    (javascriptGenerator as any).forBlock['set_aob_variable'] = function (block: any) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'), 'VARIABLE'
      );
      var value = (javascriptGenerator as any).valueToCode(
        block, 'VALUE', (javascriptGenerator as any).ORDER_ASSIGNMENT
      ) || '0';
      var v = block.workspace && block.workspace.getVariableById(block.getFieldValue('VAR'));
      var t = (v && v.type) || 'Number';
      // A null value must land as a TRUE null, not be coerced: the String/TimeStamp RHS does `'' + value`,
      // which turns null into the string "null". Detect the logic_null child and assign raw null.
      var valueBlock = block.getInputTargetBlock('VALUE');
      var rhs = (valueBlock && valueBlock.type === 'logic_null')
        ? ' = null'
        : (VAR_SET_RHS[t] || VAR_SET_RHS['Number'])(value);
      return variableName + rhs + ';\n';
    };
}

export function customBlockForGetAobVariable(host: CoreVariableBlocksHost) {
    const VAR_COLOUR: { [k: string]: any } = {
      Number: 230, String: '#5ba58c', Boolean: 0, TimeStamp: '#F5A623', GEOMETRY: '#2e7d32',
    };
    const componentRef: any = host;
    class NonEditableFieldVariable extends Blockly.FieldVariable {
      override showEditor_() {
        const blk: any = this.getSourceBlock();
        if (!blk || !componentRef) return;
        componentRef.selectedBlock = blk;
        componentRef.selectedBlockFlag = true;
        componentRef.variableToBeAdded = componentRef.variableToBeAdded || {};
        componentRef.variableToBeAdded.allowedType = 'REFERENCE_ENTITY_VARIABLE_AOB';
        componentRef.variableToBeAdded.entity = 'entity_reference_for_AOB';
        componentRef.AOBToBeAddedAllowedType = 'SCALAR';
        componentRef.AOBToBeAddedEntity = [];
        componentRef.blockAtlasField = 'VAR';
        componentRef.presentBlockVariable = blk.getFieldValue('VAR');
        componentRef.openAtlasForBlock();
      }
    }

    Blockly.Blocks['get_aob_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get')
          .appendField(
            new NonEditableFieldVariable('NAN', undefined, ['String', 'Number', 'Boolean', 'TimeStamp'], 'String'),
            'VAR'
          )
          .appendField('FROM AOB reference');
        this.setOutput(true, 'String');
        this.setTooltip('Get the value of an AOB-reference field (type follows the field)');
        this.setHelpUrl('');

        const block: any = this;
        const applyType = function (id: any) {
          const v = block.workspace && block.workspace.getVariableById(id);
          const configured = !!(v && v.name !== 'NAN' && VAR_COLOUR[v.type] != null);
          // null output when unconfigured/loading so a saved get reconnects to any socket on load;
          // the real type is applied on pick (validator) + on FINISHED_LOADING or this block's own BLOCK_CREATE below.
          block.setOutput(true, configured ? v.type : null);
          block.setColour(configured ? VAR_COLOUR[v.type] : '#9aa0a6');
        };
        this.getField('VAR').setValidator(function (newId: any) {
          applyType(newId);
          return newId;
        });
        applyType(this.getFieldValue('VAR'));
        // Re-apply the type after load or paste, on FINISHED_LOADING or this block's own BLOCK_CREATE (reappliesType),
        // NOT during deserialization.
        this.setOnChange(function (e: any) {
          if (!block.workspace || block.isInFlyout || !e) return;
          if (reappliesType(e, block)) applyType(block.getFieldValue('VAR'));
          // Was (until 2026-10-09): if (e.type === (Blockly as any).Events.FINISHED_LOADING) applyType(block.getFieldValue('VAR'));
          // — a paste fires no FINISHED_LOADING, so a pasted block stayed grey and untyped until the function was reopened.
        });
      },
    };

    (javascriptGenerator as any).forBlock['get_aob_variable'] = function (block: any) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'), 'VARIABLE'
      );
      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };
}

export function customBlockForSetNumberVariable(host: CoreVariableBlocksHost) {
    Blockly.Blocks['set_number_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .setCheck(['Number'])
          .appendField('set Number')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultNumber',
              undefined,
              ['Number'],
              'Number'
            ),
            'VAR'
          )
          .appendField('to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(230);
        this.setTooltip('Set a number value to a variable');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_number_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      var value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '0';

      return variableName + ' = ' + value + ';\n';
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForGetNumberVariable(host: CoreVariableBlocksHost) {
    // Define 'get_number_variable' block
    Blockly.Blocks['get_number_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get Number')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultNumber',
              undefined,
              ['Number'],
              'Number'
            ),
            'VAR'
          );

        this.setOutput(true, 'Number');
        this.setColour(230);
        this.setTooltip('Get the value of a number variable');
        this.setHelpUrl('');
      },
    };
    (javascriptGenerator as any).forBlock['get_number_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );

      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForSetStringVariable(host: CoreVariableBlocksHost) {
    Blockly.Blocks['set_string_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .setCheck(['String', 'Number'])
          .appendField('set string')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultString',
              undefined,
              ['String', 'Number'],
              'String'
            ),
            'VAR'
          )
          .appendField('to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#5ba58c');
        this.setTooltip('Set a String value to a variable');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_string_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      var value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '0';

      return variableName + " =  '' + " + value + ';\n';
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForGetStringVariable(host: CoreVariableBlocksHost) {
    // Define 'get_number_variable' block
    Blockly.Blocks['get_string_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get string')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultString',
              undefined,
              ['String', 'Number'],
              'String'
            ),
            'VAR'
          );

        this.setOutput(true, 'String');
        this.setColour('#5ba58c');
        this.setTooltip('Get the value of a string variable');
        this.setHelpUrl('');
      },
    };
    (javascriptGenerator as any).forBlock['get_string_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );

      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForSetTimeStampVariable(host: CoreVariableBlocksHost) {
    Blockly.Blocks['set_time_stamp_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .setCheck(['TimeStamp'])
          .appendField('set Time Stamp')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultTimeStamp',
              undefined,
              ['TimeStamp'],
              'TimeStamp'
            ),
            'VAR'
          )
          .appendField('to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#F5A623');
        this.setTooltip('Set a TimeStamp value to a variable');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_time_stamp_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      var value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '0';

      return variableName + " =  '' + " + value + ';\n';
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForGetTimeStampVariable(host: CoreVariableBlocksHost) {
    // Define 'get_number_variable' block
    Blockly.Blocks['get_time_stamp_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get timestamp')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultTimeStamp',
              undefined,
              ['TimeStamp'],
              'TimeStamp'
            ),
            'VAR'
          );

        this.setOutput(true, 'TimeStamp');
        this.setColour('#F5A623');
        this.setTooltip('Get the value of a TimeStamp variable');
        this.setHelpUrl('');
      },
    };
    (javascriptGenerator as any).forBlock['get_time_stamp_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );

      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForSetBooleanVariable(host: CoreVariableBlocksHost) {
    Blockly.Blocks['set_boolean_variable'] = {
      init: function () {
        this.appendValueInput('VALUE')
          .setCheck('Boolean')
          .appendField('set boolean')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultFlag',
              undefined,
              ['Boolean'],
              'Boolean'
            ),
            'VAR'
          )
          .appendField('to');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(0);
        this.setTooltip('Set a Boolean value to a variable');
        this.setHelpUrl('');
      },
    };

    (javascriptGenerator as any).forBlock['set_boolean_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );
      var value =
        (javascriptGenerator as any).valueToCode(
          block,
          'VALUE',
          (javascriptGenerator as any).ORDER_ASSIGNMENT
        ) || '0';

      return variableName + ' =' + value + ';\n';
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

export function customBlockForGetBooleanVariable(host: CoreVariableBlocksHost) {
    // Define 'get_number_variable' block
    Blockly.Blocks['get_boolean_variable'] = {
      init: function () {
        this.appendDummyInput()
          .appendField('get boolean')
          .appendField(
            new NonEditableFieldVariable(
              'localVariable$defaultFlag',
              undefined,
              ['Boolean'],
              'Boolean'
            ),
            'VAR'
          );
        this.setOutput(true, 'Boolean');
        this.setColour(0);
        this.setTooltip('Get the value of a boolean variable');
        this.setHelpUrl('');
      },
    };
    (javascriptGenerator as any).forBlock['get_boolean_variable'] = function (
      block: any
    ) {
      var variableName = (javascriptGenerator as any).nameDB_.getName(
        block.getFieldValue('VAR'),
        'VARIABLE'
      );

      return [variableName, (javascriptGenerator as any).ORDER_ATOMIC];
    };

    class NonEditableFieldVariable extends Blockly.FieldVariable {
      constructor(
        variableName: any,
        validator: any,
        variableTypes: any,
        defaultType: any
      ) {
        super(variableName, validator, variableTypes, defaultType);
      }

      // Override the showEditor_ method to disable editing
      override showEditor_() {
        // Do nothing to disable the variable editor
      }
    }
}

/**
 * The twelve registered together in ngOnInit (component lines 175-192), in that exact order.
 *
 * `customBlockForPushNewElementToArrayV2` is deliberately NOT here: it must run AFTER
 * EntityVariableFns.customBlockForPushNewElementToArray, because it OVERRIDES it
 * ("overrides the helper above: value-carrying push"). A caller registering the full block set must
 * keep that ordering itself, exactly as the component does at line 274.
 */
export function registerCoreVariableBlocks(host: CoreVariableBlocksHost) {
  customBlockForSetVariable(host);
  customBlockForGetVariable(host);
  customBlockForSetAobVariable(host);
  customBlockForGetAobVariable(host);
  // DEPRECATED legacy typed variable registrations -- kept so saved functions whose xml
  // references the old types still load. Same markers as the component.
  customBlockForSetNumberVariable(host);
  customBlockForGetNumberVariable(host);
  customBlockForSetStringVariable(host);
  customBlockForGetStringVariable(host);
  customBlockForSetTimeStampVariable(host);
  customBlockForGetTimeStampVariable(host);
  customBlockForSetBooleanVariable(host);
  customBlockForGetBooleanVariable(host);
}
